import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueries } from '@tanstack/react-query'
import { CalendarView } from '@/components/teacher-request/CalendarView'
import { DayLessonsPanel } from '@/components/teacher-request/DayLessonsPanel'
import { ReviewModal } from '@/components/teacher-request/ReviewModal'
import { TeacherLetterDirectory } from '@/components/teacher/TeacherLetterDirectory'
import { adminOnBehalfApi, type OnBehalfTeacher } from '@/services/api/adminOnBehalf'
import { teacherPriorityApi } from '@/services/api/teacherPriority'
import { useToast } from '@/components/ui/ToastProvider'
import { mergeMonthDays } from '@/hooks/useCalendarData'
import { formatDateLong, getDatesInRange, isLessonInPast, monthsInRange, todayLocalISO } from '@/lib/date-utils'
import { extractErrorMessage } from '@/utils/errorHandler'
import type { AbsenceType, DateRange, SelectionMode } from '@/types/teacher-request'

type LessonSel = { entryId: string; date: string; timeSlotId: string; startTime?: string; endTime?: string }

type CalendarLesson = {
  entry_id: string
  date: string
  time_slot_id: string
  time_slot_number: number
  start_time: string
  end_time: string
  subject_name: string | null
  room_name: string | null
  klass_name: string | null
  is_requestable: boolean
  substitution_excluded?: boolean
  existing_request_id: string | null
  existing_request_status: string | null
}

type CalendarDay = {
  date: string
  is_school_day: boolean
  lessons: CalendarLesson[]
}

function isSelectableLesson(l: CalendarLesson): boolean {
  return !!l.is_requestable && !isLessonInPast(l.date, l.end_time)
}

function toSelectedLesson(l: CalendarLesson): LessonSel {
  return { entryId: l.entry_id, date: l.date, timeSlotId: l.time_slot_id, startTime: l.start_time, endTime: l.end_time }
}

function collectRequestableLessons(calendarDays: CalendarDay[], dates: string[]): LessonSel[] {
  const dateSet = new Set(dates)
  const newLessons: LessonSel[] = []
  for (const day of calendarDays) {
    if (!dateSet.has(day.date)) continue
    for (const l of day.lessons ?? []) {
      if (!isSelectableLesson(l)) continue
      newLessons.push(toSelectedLesson(l))
    }
  }
  return newLessons
}

export function AdminOnBehalfRequestPage() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const now = new Date()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [teacher, setTeacher] = useState<OnBehalfTeacher | null>(null)
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [selectedDate, setSelectedDate] = useState(todayLocalISO())
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('single')
  const [dateRange, setDateRange] = useState<DateRange | null>(null)
  const [rangeStep, setRangeStep] = useState<'from' | 'to'>('from')
  const [selectedLessons, setSelectedLessons] = useState<LessonSel[]>([])
  const [showReview, setShowReview] = useState(false)
  const [absenceType, setAbsenceType] = useState<AbsenceType | ''>('')
  const [reason, setReason] = useState('')
  const [message, setMessage] = useState('')
  const [documents, setDocuments] = useState<File[]>([])
  const [skipGate, setSkipGate] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pendingDefaultLessonSelect = useRef(false)
  const userEditedSelection = useRef(false)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250)
    return () => clearTimeout(t)
  }, [query])

  const teachersQ = useQuery({
    queryKey: ['on-behalf-teachers', debouncedQuery],
    queryFn: () => adminOnBehalfApi.listTeachers(debouncedQuery || undefined),
  })
  const settingsQ = useQuery({
    queryKey: ['on-behalf-gate-settings'],
    queryFn: () => adminOnBehalfApi.gateSettings(),
  })
  const monthQ = useQuery({
    queryKey: ['on-behalf-month', teacher?.id, year, month],
    queryFn: () => adminOnBehalfApi.month(teacher!.id, year, month),
    enabled: !!teacher,
  })

  const extraMonths = useMemo(() => {
    if (selectionMode !== 'range' || !dateRange?.from || !dateRange?.to) return []
    return monthsInRange(dateRange.from, dateRange.to).filter(
      m => !(m.year === year && m.month === month),
    )
  }, [selectionMode, dateRange, year, month])

  const extraMonthQueries = useQueries({
    queries: extraMonths.map(m => ({
      queryKey: ['on-behalf-month', teacher?.id, m.year, m.month],
      queryFn: () => adminOnBehalfApi.month(teacher!.id, m.year, m.month),
      enabled: !!teacher,
    })),
  })

  const extraMonthsReady = extraMonths.length === 0 || extraMonthQueries.every(q => q.isSuccess || q.isError)

  const days: CalendarDay[] = useMemo(
    () => mergeMonthDays([monthQ.data, ...extraMonthQueries.map(q => q.data)]),
    [monthQ.data, extraMonthQueries],
  )
  const currentDay = days.find(d => d.date === selectedDate)
  const directorEnabled = !!settingsQ.data?.director_enabled
  const minDate = todayLocalISO()

  const selectedDates = useMemo(() => {
    if (selectionMode === 'range' && dateRange) {
      return getDatesInRange(dateRange.from, dateRange.to)
    }
    return selectedDate ? [selectedDate] : []
  }, [selectionMode, dateRange, selectedDate])

  useEffect(() => {
    if (!pendingDefaultLessonSelect.current || !monthQ.data || !extraMonthsReady) return
    pendingDefaultLessonSelect.current = false
    if (userEditedSelection.current) return
    setSelectedLessons(collectRequestableLessons(days, selectedDates))
  }, [monthQ.data, selectedDates, days, extraMonthsReady])

  const multiDayData = useMemo(() => {
    const data: Record<string, CalendarDay> = {}
    for (const date of selectedDates) {
      data[date] = days.find(d => d.date === date) || { date, lessons: [], is_school_day: true }
    }
    return data
  }, [selectedDates, days])

  const handleDateSelect = useCallback((date: string) => {
    if (date < todayLocalISO()) return
    if (selectionMode === 'range') {
      if (rangeStep === 'from') {
        setDateRange({ from: date, to: date })
        setRangeStep('to')
        setSelectedDate(date)
        setSelectedLessons([])
        return
      }
      let from = dateRange?.from || date
      let to = date
      if (to < from) {
        const tmp = from
        from = to
        to = tmp
      }
      setDateRange({ from, to })
      setRangeStep('from')
      setSelectedDate(from)
      const dates = getDatesInRange(from, to)
      setSelectedLessons(collectRequestableLessons(days, dates))
      pendingDefaultLessonSelect.current = true
      userEditedSelection.current = false
      return
    }
    setSelectedDate(date)
    setSelectedLessons([])
  }, [selectionMode, rangeStep, dateRange, days])

  const handleSelectionModeChange = (mode: SelectionMode) => {
    setSelectionMode(mode)
    if (mode === 'single') {
      setDateRange(null)
      setSelectedLessons([])
      setRangeStep('from')
      return
    }
    setDateRange({ from: selectedDate, to: selectedDate })
    setRangeStep('from')
    setSelectedLessons(collectRequestableLessons(days, [selectedDate]))
    pendingDefaultLessonSelect.current = true
    userEditedSelection.current = false
  }

  const applyDateRange = (range: DateRange | null) => {
    const today = todayLocalISO()
    if (range && (range.from < today || range.to < today)) return
    setDateRange(range)
    setSelectedDate(range?.from || selectedDate)
    setRangeStep('from')
    if (range) {
      pendingDefaultLessonSelect.current = true
      userEditedSelection.current = false
      setSelectedLessons(collectRequestableLessons(days, getDatesInRange(range.from, range.to)))
    } else {
      setSelectedLessons([])
    }
  }

  const toggleLesson = (lesson: CalendarLesson) => {
    if (!isSelectableLesson(lesson)) return
    userEditedSelection.current = true
    setSelectedLessons(prev => {
      const exists = prev.some(l => l.entryId === lesson.entry_id && l.date === lesson.date && (l.startTime || '') === (lesson.start_time || ''))
      if (exists) return prev.filter(l => !(l.entryId === lesson.entry_id && l.date === lesson.date && (l.startTime || '') === (lesson.start_time || '')))
      return [...prev, toSelectedLesson(lesson)]
    })
  }

  const submit = async () => {
    if (!teacher || selectedLessons.length === 0) return
    setSubmitting(true)
    setError(null)
    try {
      const inRange = selectionMode === 'range' && dateRange
      const created = await adminOnBehalfApi.create({
        teacher_id: teacher.id,
        entry_ids: [...new Set(selectedLessons.map(l => l.entryId))],
        date: inRange ? dateRange.from : selectedDate,
        date_to: inRange ? dateRange.to : null,
        lessons: selectedLessons.map(l => ({
          entry_id: l.entryId,
          date: l.date,
          start_time: l.startTime || undefined,
          end_time: l.endTime || undefined,
        })),
        absence_type: directorEnabled ? (absenceType || undefined) : undefined,
        reason: directorEnabled ? (reason || undefined) : undefined,
        message_to_substitutes: message || undefined,
      })
      if (directorEnabled) {
        for (const file of documents) {
          await teacherPriorityApi.uploadRequestDocument(created.request_id, file)
        }
      }
      const result = await adminOnBehalfApi.submit(created.request_id, skipGate)
      const omitted = (created.omitted ?? []).map(row => row.subject_name).filter(Boolean)
      const messageText = omitted.length
        ? `${result.message} Ausgelassen ohne Stellvertretung: ${omitted.join(', ')}`
        : result.message
      showToast({ message: messageText, type: omitted.length ? 'warning' : 'success', duration: 5000 })
      navigate(`/admin/substitutions?detail=${created.request_id}`)
    } catch (e) {
      setError(extractErrorMessage(e, { loadError: 'Anfrage konnte nicht erstellt werden' }))
      setSubmitting(false)
    }
  }

  useEffect(() => {
    setSelectedLessons([])
    setSelectionMode('single')
    setDateRange(null)
    setRangeStep('from')
  }, [teacher?.id])

  const teachers = teachersQ.data?.teachers ?? []

  const previewFn = useMemo(() => {
    if (!teacher) return undefined
    return (payload: { entry_ids: string[]; date?: string | null; date_to?: string | null; lessons?: { entry_id: string; date: string }[]; absence_type?: string | null }, signal?: AbortSignal) =>
      adminOnBehalfApi.gatePreview({
        teacher_id: teacher.id,
        entry_ids: payload.entry_ids,
        date: payload.date ?? undefined,
        date_to: payload.date_to,
        lessons: payload.lessons,
        absence_type: payload.absence_type,
      }, signal)
  }, [teacher])

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {!teacher ? (
        <div className="rounded-xl border border-gray-200 bg-white p-3.5">
          <TeacherLetterDirectory
            title="Lehrer wählen"
            items={teachers}
            query={query}
            onQueryChange={setQuery}
            onSelect={setTeacher}
          />
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="text-sm bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
              Sie erstellen die Anfrage im Namen von <strong>{teacher.last_name}, {teacher.first_name}</strong>
            </p>
            <button type="button" className="text-sm text-indigo-700 underline" onClick={() => setTeacher(null)}>
              Anderen Lehrer
            </button>
          </div>
          {error && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="mb-4 bg-white rounded-lg border px-4 py-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                type="button"
                onClick={() => handleSelectionModeChange('single')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  selectionMode === 'single'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Einzelner Tag
              </button>
              <button
                type="button"
                onClick={() => handleSelectionModeChange('range')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  selectionMode === 'range'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Zeitraum
              </button>
            </div>
            {selectionMode === 'single' ? (
              <input
                type="date"
                value={selectedDate}
                min={minDate}
                onChange={e => {
                  const v = e.target.value
                  if (v && v < minDate) return
                  setSelectedDate(v)
                  setSelectedLessons([])
                }}
                className="px-3 py-2 border rounded-lg text-sm"
                aria-label="Datum auswählen"
              />
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateRange?.from || ''}
                  min={minDate}
                  onChange={e => {
                    const value = e.target.value
                    if (!value || value < minDate) return
                    const to = dateRange?.to && dateRange.to >= value ? dateRange.to : value
                    applyDateRange({ from: value, to })
                  }}
                  className="px-3 py-2 border rounded-lg text-sm"
                  aria-label="Von-Datum"
                />
                <span className="text-gray-400">→</span>
                <input
                  type="date"
                  value={dateRange?.to || ''}
                  min={minDate}
                  onChange={e => {
                    const value = e.target.value
                    if (!value || value < minDate || !dateRange?.from) return
                    const from = value < dateRange.from ? value : dateRange.from
                    applyDateRange({ from, to: value })
                  }}
                  className="px-3 py-2 border rounded-lg text-sm"
                  aria-label="Bis-Datum"
                />
              </div>
            )}
            {selectedLessons.length > 0 && (
              <span className="text-sm text-blue-600 font-medium">
                {selectedLessons.length} Lektion{selectedLessons.length !== 1 ? 'en' : ''} ausgewählt
              </span>
            )}
            {selectionMode === 'range' && dateRange?.from && dateRange?.to && (
              <span className="text-sm text-gray-600">
                Zeitraum: {formatDateLong(dateRange.from)} – {formatDateLong(dateRange.to)}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <CalendarView
                month={month}
                year={year}
                days={days}
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                onPrevMonth={() => { if (month === 1) { setMonth(12); setYear(y => y - 1) } else setMonth(m => m - 1) }}
                onNextMonth={() => { if (month === 12) { setMonth(1); setYear(y => y + 1) } else setMonth(m => m + 1) }}
                onToday={() => {
                  const t = new Date()
                  setYear(t.getFullYear())
                  setMonth(t.getMonth() + 1)
                  if (selectionMode === 'single') {
                    setSelectedDate(todayLocalISO())
                    setSelectedLessons([])
                  }
                }}
                selectionMode={selectionMode}
                dateRange={dateRange}
              />
            </div>
            <div>
              <DayLessonsPanel
                date={selectedDate}
                lessons={currentDay?.lessons || []}
                selectedLessons={selectedLessons}
                onLessonToggle={toggleLesson}
                onSelectAll={() => {
                  userEditedSelection.current = true
                  const datesToProcess = selectedDates.length > 0 ? selectedDates : [selectedDate]
                  setSelectedLessons(collectRequestableLessons(days, datesToProcess))
                }}
                onDeselectAll={() => {
                  userEditedSelection.current = true
                  setSelectedLessons([])
                }}
                onSelectDay={date => {
                  userEditedSelection.current = true
                  const dayLessons = (days.find(d => d.date === date)?.lessons || [])
                    .filter(isSelectableLesson)
                    .map(toSelectedLesson)
                  setSelectedLessons(prev => [...prev.filter(l => l.date !== date), ...dayLessons])
                }}
                onDeselectDay={date => {
                  userEditedSelection.current = true
                  setSelectedLessons(prev => prev.filter(l => l.date !== date))
                }}
                selectedDates={selectedDates}
                multiDayData={multiDayData}
              />
              <button
                type="button"
                disabled={selectedLessons.length === 0 || submitting}
                onClick={() => setShowReview(true)}
                className="mt-4 w-full bg-indigo-600 text-white rounded-lg py-2.5 text-sm font-semibold disabled:opacity-40"
              >
                Anfrage prüfen
              </button>
            </div>
          </div>
          <ReviewModal
            isOpen={showReview}
            onClose={() => setShowReview(false)}
            onConfirm={submit}
            requestMode="sequential"
            customThreshold={null}
            parallelDeadline={null}
            requestFlow="automatic"
            selectedLessons={selectedLessons}
            selectedDate={selectedDate}
            selectionMode={selectionMode}
            dateRange={dateRange}
            directorEnabled={directorEnabled}
            directorSettings={settingsQ.data}
            absenceType={absenceType}
            onAbsenceTypeChange={v => setAbsenceType(v)}
            reason={reason}
            onReasonChange={setReason}
            messageToSubstitutes={message}
            onMessageToSubstitutesChange={setMessage}
            documents={documents}
            onDocumentsChange={setDocuments}
            adminMode
            skipGate={skipGate}
            onSkipGateChange={setSkipGate}
            gatePreviewFn={previewFn}
          />
        </>
      )}
    </div>
  )
}
